import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { ProgressiveImage } from './ProgressiveImage';
import { Loader } from "@googlemaps/js-api-loader";

interface ImagePreview {
  file: File;
  preview: string;
  display: File;
  thumbnail: File;
  name: string;
  description: string;
}

interface Photo {
  id: string;
  displayUrl: string;
  thumbnailUrl: string;
  name: string;
  description: string;
}

interface Restaurant {
  placeId: string;
  name: string;
  address: string;
}

export default function EditPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visitDate, setVisitDate] = useState<string>('');
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<Photo[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [existingRestaurant, setExistingRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);
  const [autocompleteInstance, setAutocompleteInstance] = useState<google.maps.places.PlaceAutocompleteElement | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!currentUser || !postId) return;

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/posts/${postId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch post');
        }

        const post = await response.json();
        setTitle(post.title);
        setContent(post.content);
        setVisitDate(post.visit_date || '');
        setExistingPhotos(post.photos || []);

        // Check for restaurant details directly on the post object
        if (post.restaurant_place_id && post.restaurant_name) {
          const restaurantData = {
            placeId: post.restaurant_place_id, // Use correct field name
            name: post.restaurant_name,         // Use correct field name
            address: post.restaurant_address || '', // Use correct field name, handle potential null/undefined address
          };
          setSelectedRestaurant(restaurantData); // Set selected state
          setExistingRestaurant(restaurantData); // Set existing state
          console.log('Existing restaurant data set (corrected parsing):', restaurantData);
        } else {
          console.log('No existing restaurant data found in API response.');
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load post');
        console.error('Error fetching post:', err);
      }
    };

    fetchPost();
  }, [currentUser, postId]);

  // Load Google Maps API
  useEffect(() => {
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!googleMapsApiKey) {
      console.error("Google Maps API Key is missing.");
      setError("Restaurant search is unavailable. Missing API Key.");
      return;
    }

    const loader = new Loader({
      apiKey: googleMapsApiKey,
      version: "weekly",
      libraries: ["places"],
    });

    loader.load()
      .then(() => {
        console.log("Google Maps API loaded successfully");
        setIsMapsApiLoaded(true);
      })
      .catch(e => {
        console.error("Error loading Google Maps API:", e);
        setError("Failed to load restaurant search functionality.");
      });
  }, []);

  // Effect 1: Initialize Autocomplete Element when API and Ref are ready
  useEffect(() => {
    let retryTimeoutId: NodeJS.Timeout | null = null;
    let placeAutocompleteElement: google.maps.places.PlaceAutocompleteElement | null = null;
    let gmpSelectListener: ((event: Event) => void) | null = null;

    const initialize = () => {
      // Check conditions upfront
      if (!isMapsApiLoaded) {
        console.log(`Init Effect: Skipping, API not loaded yet.`);
        return; // Exit if API not loaded
      }

      const container = autocompleteContainerRef.current;
      if (!container) {
        console.log(`Init Effect: Ref not available yet. Retrying in 100ms...`);
        retryTimeoutId = setTimeout(initialize, 100); // Retry if ref not ready
        return;
      }

      // --- Proceed with initialization --- 
      console.log('Effect 1: API loaded and Ref available. Initializing PlaceAutocompleteElement...');
      const google = window.google;

      if (!google?.maps?.places?.PlaceAutocompleteElement) {
        console.error("PlaceAutocompleteElement not available.");
        setError("Restaurant search component is currently unavailable.");
        return;
      }

      try {
        container.innerHTML = ''; // Clear previous content

        placeAutocompleteElement = new google.maps.places.PlaceAutocompleteElement({
          requestedLanguage: 'en',
          types: ['establishment'],
          componentRestrictions: { country: 'us' }
        });

        // Add styling
        placeAutocompleteElement.style.width = '100%';
        placeAutocompleteElement.style.padding = '8px';
        placeAutocompleteElement.style.borderRadius = '0.375rem';
        placeAutocompleteElement.style.border = '1px solid #d1d5db';
        placeAutocompleteElement.style.outline = 'none';
        placeAutocompleteElement.style.boxSizing = 'border-box';
        placeAutocompleteElement.setAttribute('placeholder', 'Search for a restaurant...');

        container.appendChild(placeAutocompleteElement);
        setAutocompleteInstance(placeAutocompleteElement); // Store instance in state
        console.log('Effect 1: PlaceAutocompleteElement instance stored in state.');

        // Define and add listener
        gmpSelectListener = async (event: Event) => {
          const selectEvent = event as CustomEvent<{ placePrediction: google.maps.places.PlacePrediction | null }>;
          const placePrediction = selectEvent.detail?.placePrediction ?? (event as any).placePrediction as google.maps.places.PlacePrediction | null;

          if (!placePrediction) {
            console.error('No place prediction found in the selection event');
            setSelectedRestaurant(null);
            return;
          }
          const place = placePrediction.toPlace();
          try {
            await place.fetchFields({ fields: ['id', 'displayName', 'formattedAddress'] });
            if (place.id && place.displayName && place.formattedAddress) {
              const restaurantData = {
                placeId: place.id,
                name: place.displayName,
                address: place.formattedAddress,
              };
              setSelectedRestaurant(restaurantData);
              setExistingRestaurant(restaurantData); // Update existingRestaurant as well on new selection
              setError('');
            } else {
              console.error('Incomplete place details after fetching');
              setSelectedRestaurant(null);
              setError('Could not retrieve full restaurant details.');
            }
          } catch (error) {
            console.error('Error processing place details:', error);
            setError('Failed to process restaurant details');
            setSelectedRestaurant(null);
          }
        };
        placeAutocompleteElement.addEventListener('gmp-select', gmpSelectListener);
        console.log('Effect 1: gmp-select listener added.');

      } catch (error) {
        console.error("Effect 1: Error creating or appending PlaceAutocompleteElement:", error);
        setError("Failed to initialize restaurant search.");
        setAutocompleteInstance(null); // Clear instance on error
      }
    };

    initialize(); // Start the initialization attempt

    // Cleanup function for Effect 1
    return () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId); // Clear retry timeout if active
      }
      
      // Cleanup logic for the listener and DOM element
      if (placeAutocompleteElement && gmpSelectListener) {
        placeAutocompleteElement.removeEventListener('gmp-select', gmpSelectListener);
        console.log('Effect 1 Cleanup: gmp-select listener removed.');
      }
      const container = autocompleteContainerRef.current;
      if (container && placeAutocompleteElement && container.contains(placeAutocompleteElement)) {
         container.removeChild(placeAutocompleteElement);
         console.log('Effect 1 Cleanup: Autocomplete DOM element removed.');
      }
      setAutocompleteInstance(null); // Clear instance from state
      console.log('Effect 1 Cleanup: Autocomplete instance cleared from state.');
    };
  }, [isMapsApiLoaded]); // Keep dependency only on isMapsApiLoaded

  // Effect 2: Update Autocomplete Value when instance or data changes
  useEffect(() => {
    // Removed loading check, as existingRestaurant is set correctly now
    if (autocompleteInstance) {
        const input = autocompleteInstance.querySelector('input');
        if (!input) {
            console.log('Effect 2: Could not find inner input element.');
            return;
        }

        if (existingRestaurant) {
            // Set the initial value only if the input is currently empty
            if (input.value === '') {
                console.log(`Effect 2: Setting initial input value: ${existingRestaurant.name}`);
                input.value = existingRestaurant.name;
            } else {
                console.log(`Effect 2: Input not empty ('${input.value}'), skipping initial set.`);
            }
        } else {
            // Clear input value if existingRestaurant is null/cleared
            console.log('Effect 2: Clearing input value because existingRestaurant is null.');
            input.value = '';
        }
    }
  }, [autocompleteInstance, existingRestaurant]); // Dependencies: instance and restaurant data

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newImages = Array.from(e.target.files).map(file => {
      const preview = URL.createObjectURL(file);
      const display = new File([file], `display_${file.name}`, { type: file.type });
      const thumbnail = new File([file], `thumbnail_${file.name}`, { type: file.type });
      return {
        file,
        preview,
        display,
        thumbnail,
        name: '',
        description: '',
      };
    });

    setImages(prev => [...prev, ...newImages]);
  };

  const handleImageMetadataChange = (index: number, field: 'name' | 'description', value: string) => {
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, [field]: value } : img
    ));
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoId: string) => {
    setExistingPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !postId) return;

    try {
      setError('');
      setLoading(true);

      // Validate required fields
      if (!title.trim()) {
        setError('Title is required');
        setLoading(false);
        return;
      }

      if (!content.trim()) {
        setError('Content is required');
        setLoading(false);
        return;
      }

      // Validate image metadata
      const hasInvalidMetadata = [...images, ...existingPhotos].some(photo => 
        !photo.name.trim() && photo.description.trim()
      );
      
      if (hasInvalidMetadata) {
        setError('Please provide a name for all photos');
        setLoading(false);
        return;
      }

      const idToken = await currentUser.getIdToken();
      const formData = new FormData();

      // Append files and create metadata array
      const photoMetadata: { 
        filename: string, 
        name: string, 
        description: string,
        displayFilename: string,
        thumbnailFilename: string
      }[] = [];

      // Add metadata for existing photos
      existingPhotos.forEach(photo => {
        photoMetadata.push({
          filename: photo.id, // Using photo.id as filename for existing photos
          displayFilename: photo.displayUrl,
          thumbnailFilename: photo.thumbnailUrl,
          name: photo.name,
          description: photo.description
        });
      });

      // Add metadata for new images
      images.forEach((image, index) => {
        formData.append('displayImages', image.display, `display_${image.file.name}`);
        formData.append('thumbnailImages', image.thumbnail, `thumbnail_${image.file.name}`);
        
        photoMetadata.push({
          filename: image.file.name,
          displayFilename: `display_${image.file.name}`,
          thumbnailFilename: `thumbnail_${image.file.name}`,
          name: image.name,
          description: image.description
        });
      });

      // Append metadata as a JSON string
      formData.append('photoMetadata', JSON.stringify(photoMetadata));
      formData.append('title', title);
      formData.append('content', content);
      formData.append('visit_date', visitDate);
      formData.append('existingPhotoIds', JSON.stringify(existingPhotos.map(p => p.id)));
      formData.append('existingPhotoMetadata', JSON.stringify(existingPhotos.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description
      }))));

      if (selectedRestaurant) {
        formData.append('placeId', selectedRestaurant.placeId);
        formData.append('restaurantName', selectedRestaurant.name);
        formData.append('restaurantAddress', selectedRestaurant.address);
      } else if (existingRestaurant) {
        // If there was a restaurant but it's being removed
        formData.append('removeRestaurant', 'true');
      }

      const response = await fetch(`${API_BASE_URL}/api/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update post');
      }

      navigate('/profile');
    } catch (err) {
      setError('Failed to update post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading post...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow px-6 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Post</h2>
          
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter post title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visit Date
              </label>
              <div className="date-picker-container">
                <input
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 date-picker"
                  placeholder="Select visit date"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restaurant
              </label>
              {existingRestaurant && selectedRestaurant?.placeId === existingRestaurant.placeId && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{existingRestaurant.name}</p>
                      <p className="text-sm text-gray-600">{existingRestaurant.address}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRestaurant(null);
                        setExistingRestaurant(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              <div className="relative">
                {!isMapsApiLoaded ? (
                  // Show loading state if API is not loaded
                  <div className="p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">Loading restaurant search...</p>
                  </div>
                ) : (
                  // Render the container div ONLY when API is loaded
                  <div
                    ref={autocompleteContainerRef}
                    className="w-full bg-white"
                    style={{ minHeight: '40px' }}
                  />
                )}
              </div>
              {/* Display details of the *currently* selected restaurant ONLY if it's different from the initial one (or if there was no initial one) */}
              {selectedRestaurant && (!existingRestaurant || selectedRestaurant.placeId !== existingRestaurant.placeId) && (
                <div className="mt-2 text-sm text-gray-600 bg-gray-100 p-2 rounded-md">
                  Selected: {selectedRestaurant.name} ({selectedRestaurant.address})
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Existing Photos
              </label>
              {/* Use a single-column grid, allowing each flex item to span full width */}
              <div className="grid grid-cols-1 gap-4">
                {existingPhotos.map((photo) => (
                  // Flex container for each photo item: column on small screens, row on medium+
                  <div key={photo.id} className="flex flex-col md:flex-row bg-white rounded-lg shadow overflow-hidden">
                    {/* Image container: Fixed size, allows button positioning */}
                    <div className="relative flex-shrink-0 w-full md:w-48 h-48">
                      <ProgressiveImage
                        src={`${API_BASE_URL}/api/posts/images/${photo.displayUrl}`}
                        thumbnailSrc={`${API_BASE_URL}/api/posts/images/${photo.thumbnailUrl}`}
                        alt={photo.name || 'Photo'}
                        // Use object-cover and ensure full height/width of container
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingPhoto(photo.id)}
                        // Positioned relative to the image container
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm z-10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    {/* Text input container: Grows to fill space, padding */}
                    <div className="flex-grow p-4 space-y-3">
                      <input
                        type="text"
                        value={photo.name}
                        onChange={(e) => {
                          setExistingPhotos(prev => prev.map(p =>
                            p.id === photo.id ? { ...p, name: e.target.value } : p
                          ));
                        }}
                        placeholder="Photo name (required)"
                        required // Add required attribute
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <textarea
                        value={photo.description}
                        onChange={(e) => {
                          setExistingPhotos(prev => prev.map(p =>
                            p.id === photo.id ? { ...p, description: e.target.value } : p
                          ));
                        }}
                        placeholder="Photo description"
                        rows={3} // Adjusted rows for potentially less vertical space
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add New Photos
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload images</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            </div>

            {images.length > 0 && (
              <div className="mt-6 space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Edit New Image Details
                </label>
                {images.map((image, index) => (
                  <div key={index} className="flex items-start space-x-4 border p-4 rounded-md bg-gray-50">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <ProgressiveImage
                        src={image.preview}
                        thumbnailSrc={URL.createObjectURL(image.thumbnail)}
                        alt={`Preview ${index + 1}`}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                    <div className="flex-grow space-y-2">
                      <input
                        type="text"
                        value={image.name}
                        onChange={(e) => handleImageMetadataChange(index, 'name', e.target.value)}
                        placeholder="Dish Name (e.g., Margherita Pizza)"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <textarea
                        value={image.description}
                        onChange={(e) => handleImageMetadataChange(index, 'description', e.target.value)}
                        placeholder="Description (optional)"
                        rows={2}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="text-red-500 hover:text-red-700 p-1 rounded-full self-start"
                      title="Remove image"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating post...' : 'Update Post'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 